// src/data/AgenticAIPromptData.js

const agenticAIIntro = {
  title: "Business Owners - Ask Thinky Anytime",
  description: [
    "YOUR BUSINESS AND PERSONAL ASSISTANT AI — created and perpetually evolving with more learnings and capabilities.",
    "These are indicative prompts you can give to your AI Agent. Thinky will learn and evolve to serve you with your data.",
    "Your Thinky was born to be smart and useful — it upgrades itself with your love.",
    "Currently it knows English but learning local languages.",
    "Thinky is currently built for typed instructions, and is upgrading itself to listen also.",
    "We handle all data with strict confidentiality, transparent consent, and responsible use to ensure safety, privacy, and ethical integrity.",
    "Built for humanity by Think5Whys Global DeepTech Solutions, Mumbai, India.",
    "🇮🇳 Made in India. Made for the World.",
    "Simply say — ‘I Love You Thinky’ — and get going!"
  ]
};

const agenticAIPromptData = [
  {
    category: "Quick Use Cases",
    subcategories: [
      {
        id: 1,
        title: "Daily Business Operations",
        prompts: [
          {
            id: 101,
            text: "Show me all my outstanding invoices overdue by more than 3 months. Draft reminder emails for each customer and give me Call + WhatsApp buttons to follow up.",
            plan: "Identify and list all invoices overdue beyond 90 days, generate personalized reminder emails to encourage payment, and integrate quick-action contact buttons for direct follow-up via phone or messaging.",
            actions: [
              "Query the invoicing system for all unpaid invoices older than 90 days",
              "Extract customer details including contact information for each overdue invoice",
              "Draft customized reminder emails referencing invoice details, due dates, and polite payment requests",
              "Generate embedded Call and WhatsApp buttons linked to customer phone numbers"
            ],
            outcome: "A comprehensive list of overdue invoices with ready-to-send email drafts and one-click contact options, enabling efficient collection efforts and improved cash flow.",
            steps: [
              "Step 1: Access the accounting database and filter invoices by overdue status exceeding 3 months.",
              "Step 2: Compile a sorted list by amount or date, including customer names and totals.",
              "Step 3: For each invoice, create a tailored email template with subject, body, and attachments if needed.",
              "Step 4: Embed interactive buttons for Call (tel:) and WhatsApp (whatsapp:) protocols in the email or dashboard view.",
              "Step 5: Display the full report in a user-friendly interface for review and action."
            ]
          },
          {
            id: 102,
            text: "Give me my daily dashboard—sales done, orders pending, cash in bank, collections, payables due today, and any urgent issues.",
            plan: "Aggregate key daily metrics from various business systems into a single, visual dashboard, highlighting completed sales, pending orders, current cash position, collection status, immediate payables, and flagging urgent alerts.",
            actions: [
              "Pull sales data for today's completed transactions from the CRM or sales log",
              "Retrieve pending orders from the order management system",
              "Query bank API or ledger for current cash balance",
              "Summarize collections progress and payables due today from accounting records",
              "Scan for urgent issues like stock shortages or deadline breaches across systems"
            ],
            outcome: "A real-time, at-a-glance dashboard providing actionable insights into daily operations, allowing quick decision-making to maintain business momentum.",
            steps: [
              "Step 1: Connect to relevant data sources (sales, orders, banking, accounting).",
              "Step 2: Calculate metrics: sales total, pending count/value, cash balance, collections % due, payables list.",
              "Step 3: Identify urgent issues via rule-based alerts (e.g., payables > threshold).",
              "Step 4: Format into a dashboard with charts, tables, and color-coded highlights.",
              "Step 5: Present via web/app interface with refresh options."
            ]
          },
          {
            id: 103,
            text: "List all items running below minimum stock level and prepare a purchase order draft for each supplier.",
            plan: "Scan inventory levels against predefined minimum thresholds, identify low-stock items, and auto-generate draft purchase orders tailored to each item's primary supplier.",
            actions: [
              "Compare current stock quantities to minimum reorder levels in the inventory database",
              "Group low-stock items by supplier for efficient ordering",
              "Calculate required quantities based on lead times and average sales",
              "Draft purchase orders with item details, quantities, pricing, and terms",
              "Format drafts for easy review and submission"
            ],
            outcome: "An immediate list of low-stock alerts with pre-filled purchase order drafts, preventing stockouts and streamlining procurement processes.",
            steps: [
              "Step 1: Query inventory system for all items where current stock < min level.",
              "Step 2: For each item, determine optimal reorder quantity using historical data.",
              "Step 3: Associate items with suppliers and compile per-supplier order lists.",
              "Step 4: Generate PDF or editable drafts including headers, line items, totals, and signatures.",
              "Step 5: Notify user with list and links to drafts for approval."
            ]
          },
          {
            id: 104,
            text: "Generate a simple cash flow projection for the next 14 days based on receivables, payables, staff salaries and loan EMIs.",
            plan: "Forecast cash inflows and outflows over the next two weeks by integrating expected receivables, scheduled payables, payroll, and EMI payments, presenting a net cash position timeline.",
            actions: [
              "Gather upcoming receivables from invoice due dates and collection probabilities",
              "List payables, salaries, and EMIs scheduled within 14 days from accounting calendar",
              "Calculate daily net cash movements by subtracting outflows from inflows",
              "Account for any known variables like delays or early payments",
              "Visualize projection with a simple line chart or table"
            ],
            outcome: "A clear 14-day cash flow forecast highlighting potential shortfalls or surpluses, aiding in liquidity management and financial planning.",
            steps: [
              "Step 1: Extract receivables data: due dates, amounts, and estimated collection timelines.",
              "Step 2: Compile outflows: payables due, salary dates/amounts, EMI schedules.",
              "Step 3: Build a daily timeline spreadsheet or model summing inflows minus outflows.",
              "Step 4: Apply basic assumptions (e.g., 80% on-time collections) for accuracy.",
              "Step 5: Output as a report with summary metrics like min balance and alerts."
            ]
          },
          {
            id: 105,
            text: "Tell me all compliance and filing deadlines coming up this month. Prepare any pending returns for approval.",
            plan: "Review regulatory calendars and business records to list all upcoming compliance deadlines, identify pending filings, and pre-populate return forms for quick approval and submission.",
            actions: [
              "Scan compliance calendar for monthly deadlines (taxes, reports, licenses)",
              "Check filing status in document management system for incompletes",
              "Gather required data (financials, logs) to auto-fill return templates",
              "Generate draft documents with placeholders for signatures",
              "Prioritize by urgency and notify user"
            ],
            outcome: "A prioritized list of deadlines with prepared drafts, ensuring timely compliance and avoiding penalties.",
            steps: [
              "Step 1: Access compliance tracker and filter events for current month.",
              "Step 2: Cross-reference with records to flag pending items.",
              "Step 3: Use data from ledgers/emails to populate standard return forms.",
              "Step 4: Review drafts for completeness and attach supporting docs.",
              "Step 5: Present via dashboard with approval workflow and submission links."
            ]
          }
        ]
      },
      {
        id: 2,
        title: "Sales, Customers & Leads",
        prompts: [
          {
            id: 201,
            text: "Based on my emails, messages, and pending jobs, generate today’s priority task list.",
            plan: "Analyze recent communications and job statuses to extract actionable items, prioritize by urgency and impact, and compile a focused daily task list.",
            actions: [
              "Parse emails and messages for keywords indicating tasks or follow-ups",
              "Review pending jobs in project tracker for status updates needed",
              "Score tasks by deadlines, customer value, and effort required",
              "Group into categories like calls, emails, or meetings",
              "Format as a ranked checklist with estimated times"
            ],
            outcome: "A concise, prioritized task list derived from scattered inputs, boosting productivity and ensuring no leads fall through cracks.",
            steps: [
              "Step 1: Integrate data from email client, messaging apps, and job board.",
              "Step 2: Use NLP to identify tasks, owners, and deadlines.",
              "Step 3: Apply prioritization algorithm (e.g., Eisenhower matrix).",
              "Step 4: Create interactive list with checkboxes and reminders.",
              "Step 5: Integrate with calendar for scheduling."
            ]
          },
          {
            id: 202,
            text: "Give me the list of my top 20 customers with pending quotations. Draft WhatsApp + email follow-ups.",
            plan: "Rank customers by revenue or potential, filter for those with open quotes, and create personalized follow-up messages via email and WhatsApp to nudge conversions.",
            actions: [
              "Query CRM for top 20 customers based on historical spend",
              "Filter for quotes in 'pending' status over X days",
              "Personalize drafts referencing quote details and value props",
              "Generate dual-channel messages: formal email and casual WhatsApp",
              "Include CTAs like 'reply to confirm' or schedule calls"
            ],
            outcome: "Targeted follow-ups for high-value leads, increasing quotation acceptance rates and sales pipeline velocity.",
            steps: [
              "Step 1: Extract customer data from CRM, sort by metrics like lifetime value.",
              "Step 2: Identify pending quotes via status and age filters.",
              "Step 3: Template messages with merge fields for personalization.",
              "Step 4: Format WhatsApp as quick texts, email as HTML with attachments.",
              "Step 5: Provide batch send options or individual previews."
            ]
          },
          {
            id: 203,
            text: "Summarize today’s sales team activities—calls, leads, meetings, pipeline, and red flags.",
            plan: "Consolidate sales team logs from CRM, calendars, and reports into a daily summary, covering key activities, progress, and potential issues for oversight.",
            actions: [
              "Aggregate call logs, lead entries, and meeting outcomes from team tools",
              "Calculate pipeline metrics: new leads, stage advancements, win probabilities",
              "Scan for red flags like stalled deals or low activity",
              "Visualize with metrics, charts, and narrative highlights",
              "Distribute or display for team review"
            ],
            outcome: "An insightful daily sales recap empowering managers to celebrate wins, address gaps, and adjust strategies promptly.",
            steps: [
              "Step 1: Pull data from sales platforms (e.g., calls from dialer, meetings from calendar).",
              "Step 2: Compute aggregates: total calls, leads generated, pipeline value changes.",
              "Step 3: Flag anomalies (e.g., reps below quota, deals at risk).",
              "Step 4: Structure summary with sections and visuals.",
              "Step 5: Email or dashboard push with action recommendations."
            ]
          },
          {
            id: 204,
            text: "Show me all open customer complaints and generate replies + resolution steps.",
            plan: "Retrieve active complaints from support tickets, craft empathetic response templates, and outline step-by-step resolution paths to enhance customer satisfaction.",
            actions: [
              "Query support system for open/pending complaints",
              "Categorize by type (product, service, billing) and severity",
              "Draft replies acknowledging issues and committing to action",
              "Define resolution workflows with timelines and responsible parties",
              "Track for follow-up until closure"
            ],
            outcome: "Proactive complaint management with ready responses and plans, turning negatives into loyalty opportunities.",
            steps: [
              "Step 1: Filter tickets database for status 'open' or 'in progress'.",
              "Step 2: Summarize each complaint with details and customer info.",
              "Step 3: Generate personalized reply scripts using empathy frameworks.",
              "Step 4: Map out 3-5 step resolution plans (e.g., investigate, refund, follow-up).",
              "Step 5: Assign and notify team, with user approval for sends."
            ]
          },
          {
            id: 205,
            text: "Create 10 social media posts for my MSME for this week with captions + hashtags.",
            plan: "Brainstorm engaging content ideas aligned with MSME brand, schedule 10 posts across platforms, including compelling captions and relevant hashtags for visibility.",
            actions: [
              "Research trending topics and audience interests for MSME niche",
              "Generate diverse post types: promotional, educational, behind-the-scenes",
              "Write catchy captions with calls-to-action",
              "Select 5-10 targeted hashtags per post",
              "Suggest visuals or links if applicable"
            ],
            outcome: "A week's worth of ready-to-post content boosting online engagement, brand awareness, and potential leads.",
            steps: [
              "Step 1: Analyze business profile for themes (products, tips, stories).",
              "Step 2: Outline 10 post ideas covering Mon-Sun schedule.",
              "Step 3: Craft captions (100-150 chars) with emojis and questions.",
              "Step 4: Append hashtags like #MSME #SmallBusiness #IndustryTips.",
              "Step 5: Compile into a calendar view with copy-paste ready formats."
            ]
          }
        ]
      },
      {
        id: 3,
        title: "Suppliers, Inventory & Production",
        prompts: [
          {
            id: 301,
            text: "Show supplier payments due this week and suggest a payment plan based on bank balance.",
            plan: "List weekly due payments to suppliers, assess current bank liquidity, and propose an optimized payment schedule to manage cash flow without penalties.",
            actions: [
              "Retrieve due payments from accounts payable ledger for the week",
              "Check real-time bank balance via integration",
              "Prioritize payments by due date, supplier terms, and discounts",
              "Simulate scenarios for partial/full payments",
              "Recommend sequence with rationale"
            ],
            outcome: "A feasible payment plan ensuring supplier relationships are maintained while preserving working capital.",
            steps: [
              "Step 1: Query AP system for invoices due within 7 days.",
              "Step 2: Fetch current and projected bank balances.",
              "Step 3: Sort payments by urgency (e.g., net-30 vs. COD).",
              "Step 4: Generate plan table: date, amount, method, notes.",
              "Step 5: Alert for low balance risks and alternatives like negotiations."
            ]
          },
          {
            id: 302,
            text: "Give me production status of all running jobs — delays & bottlenecks.",
            plan: "Monitor active production jobs for progress, identify delays or resource bottlenecks, and report with mitigation suggestions.",
            actions: [
              "Pull job statuses from production management software",
              "Calculate % complete and estimated completion times",
              "Flag delays against schedules and root causes (e.g., material shortage)",
              "Highlight bottlenecks like machine downtime or staffing",
              "Suggest fixes like reallocation or expediting"
            ],
            outcome: "Real-time visibility into production health, enabling swift interventions to meet delivery commitments.",
            steps: [
              "Step 1: Access ERP or shop floor system for active jobs.",
              "Step 2: Compute variances: actual vs. planned progress.",
              "Step 3: Analyze logs for bottleneck indicators (wait times, errors).",
              "Step 4: Compile report with Gantt-like views and alerts.",
              "Step 5: Propose actions and assign to team leads."
            ]
          },
          {
            id: 303,
            text: "Rank suppliers based on delivery time, quality issues, cost and reliability.",
            plan: "Evaluate suppliers using multi-criteria scoring on key performance indicators, rank them, and provide insights for sourcing decisions.",
            actions: [
              "Gather data: avg delivery days, defect rates, pricing history, uptime %",
              "Assign weights to criteria (e.g., reliability 30%, cost 25%)",
              "Calculate composite scores for each supplier",
              "Rank from best to worst with percentile bands",
              "Recommend top alternatives or improvements"
            ],
            outcome: "A data-driven supplier ranking to optimize procurement, reduce risks, and cut costs.",
            steps: [
              "Step 1: Extract metrics from supplier database and transaction logs.",
              "Step 2: Normalize data for fair comparison (e.g., z-scores).",
              "Step 3: Apply weighted scoring model.",
              "Step 4: Visualize as leaderboard table or chart.",
              "Step 5: Include qualitative notes and re-evaluation triggers."
            ]
          },
          {
            id: 304,
            text: "Show inventory valuation category-wise and highlight dead stock.",
            plan: "Categorize inventory by type, compute total values using FIFO/LIFO, and spotlight slow-moving or obsolete items for clearance.",
            actions: [
              "Group items by categories (e.g., raw materials, finished goods)",
              "Calculate current value per category from qty * unit cost",
              "Identify dead stock: items with no movement >6 months",
              "Flag high-value dead stock for priority action",
              "Suggest valuation adjustments or write-offs"
            ],
            outcome: "A categorized inventory snapshot revealing tied-up capital in dead stock, guiding stock optimization.",
            steps: [
              "Step 1: Query inventory system for all items and costs.",
              "Step 2: Aggregate values by predefined categories.",
              "Step 3: Analyze sales history to tag dead/slow stock.",
              "Step 4: Present pie chart/table with highlights.",
              "Step 5: Generate clearance recommendations (discounts, bundles)."
            ]
          },
          {
            id: 305,
            text: "Compare last 3 months’ purchase prices and show cost-saving opportunities.",
            plan: "Trend purchase prices over recent quarters, detect variances or increases, and identify negotiation or alternative supplier opportunities.",
            actions: [
              "Extract purchase orders from last 90 days by item/supplier",
              "Compute average, min/max prices per period",
              "Spot anomalies: rises >10%, better deals elsewhere",
              "Calculate potential savings if switching/renegotiating",
              "List actionable opportunities with evidence"
            ],
            outcome: "Insights into price trends with quantified savings potential, driving cost reductions in procurement.",
            steps: [
              "Step 1: Pull PO data filtered by date range and item codes.",
              "Step 2: Analyze trends using time-series (monthly averages).",
              "Step 3: Compare against benchmarks or competitors.",
              "Step 4: Highlight variances in a comparison table.",
              "Step 5: Prioritize savings plays with estimated ROI."
            ]
          }
        ]
      },
      {
        id: 4,
        title: "Financial Reports & Planning",
        prompts: [
          {
            id: 401,
            text: "Generate a spending analysis of the last 30 days and suggest cost-saving ideas.",
            plan: "Break down expenses by category over the past month, visualize trends, and recommend targeted reductions based on benchmarks.",
            actions: [
              "Categorize transactions from bank/ledger (e.g., utilities, marketing)",
              "Calculate totals, % of budget, and variances",
              "Identify high-spend areas with growth rates",
              "Benchmark against industry averages",
              "Propose 3-5 ideas like vendor switches or automation"
            ],
            outcome: "Detailed spending insights with practical savings strategies, fostering fiscal discipline.",
            steps: [
              "Step 1: Import 30-day transaction data.",
              "Step 2: Classify and sum by expense type.",
              "Step 3: Chart breakdowns (bar/pie).",
              "Step 4: Analyze for outliers and trends.",
              "Step 5: Generate tailored recommendations with projected impacts."
            ]
          },
          {
            id: 402,
            text: "Generate a simple P&L summary for this month—sales, expenses, margins, top customers/products.",
            plan: "Compile monthly profit and loss statement highlighting revenue streams, cost breakdowns, profitability margins, and key performers.",
            actions: [
              "Sum sales by customer/product from invoices",
              "Aggregate expenses from ledgers",
              "Compute gross/net margins and variances",
              "Rank top contributors (Pareto analysis)",
              "Format as concise report with KPIs"
            ],
            outcome: "A snapshot P&L revealing financial performance drivers and areas for growth.",
            steps: [
              "Step 1: Filter data for current month.",
              "Step 2: Calculate revenue minus COGS for gross profit.",
              "Step 3: Subtract operating expenses for net.",
              "Step 4: List top 5 customers/products by contribution.",
              "Step 5: Visualize with tables and gauges."
            ]
          },
          {
            id: 403,
            text: "Remind me of all loan EMIs due this month and show expected cash position after paying them.",
            plan: "List EMI schedules, project payments' impact on cash, and forecast post-payment balances to ensure liquidity.",
            actions: [
              "Retrieve loan details and EMI calendars",
              "Calculate total monthly outflow",
              "Simulate cash flow subtracting EMIs from inflows",
              "Flag if post-payment balance < threshold",
              "Suggest prepayment or refinance options"
            ],
            outcome: "EMI reminders with cash impact visualization, preventing overdrafts.",
            steps: [
              "Step 1: Access loan management records.",
              "Step 2: List EMIs by date/amount.",
              "Step 3: Integrate with cash forecast model.",
              "Step 4: Compute ending balances daily/weekly.",
              "Step 5: Alert and present calendar view."
            ]
          },
          {
            id: 404,
            text: "Generate a quotation for customer with pricing, taxes, and terms.",
            plan: "Create a professional quote document based on customer request, including itemized pricing, applicable taxes, and standard terms for approval.",
            actions: [
              "Gather quote specs from customer inquiry",
              "List products/services with quantities and rates",
              "Apply taxes/VAT and discounts",
              "Add terms: validity, payment, delivery",
              "Export as PDF with branding"
            ],
            outcome: "A polished quotation ready for sending, streamlining sales processes.",
            steps: [
              "Step 1: Input customer and line item details.",
              "Step 2: Compute subtotals, taxes, grand total.",
              "Step 3: Insert legal terms and conditions.",
              "Step 4: Review for accuracy and format.",
              "Step 5: Generate and email draft."
            ]
          },
          {
            id: 405,
            text: "Check my business health today and highlight any red flags—low stock, overdue invoices, unpaid taxes.",
            plan: "Perform a holistic health check across key areas, score overall status, and escalate critical red flags with remediation steps.",
            actions: [
              "Run diagnostics: stock levels, AR aging, tax filings",
              "Score each metric (green/yellow/red)",
              "Compile dashboard with trends",
              "Prioritize flags by impact",
              "Suggest immediate actions"
            ],
            outcome: "Proactive health alert system maintaining operational stability.",
            steps: [
              "Step 1: Query multiple systems for indicators.",
              "Step 2: Apply thresholds (e.g., stock <10%, AR >60 days).",
              "Step 3: Calculate composite health score.",
              "Step 4: Highlight reds in bold with details.",
              "Step 5: Auto-notify and log for trends."
            ]
          }
        ]
      },
      {
        id: 5,
        title: "HR, Team & Productivity",
        prompts: [
          {
            id: 501,
            text: "Summarize attendance, leaves, and pending tasks of each team member.",
            plan: "Aggregate HR data on attendance patterns, leave balances, and task backlogs per employee, providing a team overview for management.",
            actions: [
              "Pull attendance logs from time-tracking system",
              "Check leave requests/approvals and balances",
              "List pending tasks from project tools by assignee",
              "Calculate metrics like absenteeism rates",
              "Flag underperformers or overloads"
            ],
            outcome: "Team productivity snapshot aiding in resource allocation and performance discussions.",
            steps: [
              "Step 1: Integrate HRIS and task management data.",
              "Step 2: Per-employee: hours worked, leaves taken/pending, open tasks count.",
              "Step 3: Aggregate team stats.",
              "Step 4: Table format with color codes.",
              "Step 5: Export or dashboard view."
            ]
          },
          {
            id: 502,
            text: "Show shortlisted candidates for a role, prepare interview questions, and schedule interviews.",
            plan: "Display candidate pool from ATS, generate role-specific questions, and coordinate calendars for interviews.",
            actions: [
              "Filter shortlist from applicant database",
              "Create 10-15 questions (behavioral, technical)",
              "Check availability via calendar sync",
              "Propose time slots and send invites",
              "Prepare scorecards"
            ],
            outcome: "Streamlined hiring process from shortlist to scheduled interviews.",
            steps: [
              "Step 1: Query ATS for role's shortlisted profiles.",
              "Step 2: Tailor questions to JD requirements.",
              "Step 3: Poll calendars for mutual slots.",
              "Step 4: Draft invites with agendas.",
              "Step 5: Confirm and remind participants."
            ]
          },
          {
            id: 503,
            text: "Create reminders for every overdue task and automatically nudge me if something is delayed.",
            plan: "Scan task lists for overdue items, set up escalating reminders, and implement auto-nudges via preferred channels.",
            actions: [
              "Identify overdue tasks from todo/project apps",
              "Categorize by priority and owner",
              "Schedule reminders (email, app push)",
              "Set nudge rules (e.g., daily after 1 day late)",
              "Track resolution and report"
            ],
            outcome: "Automated accountability system reducing task slippage.",
            steps: [
              "Step 1: Sync with task trackers for due dates.",
              "Step 2: Filter for past due/uncompleted.",
              "Step 3: Generate personalized reminder texts.",
              "Step 4: Integrate with notification services.",
              "Step 5: Monitor and escalate if ignored."
            ]
          },
          {
            id: 504,
            text: "Summarize last 10 emails and create an action plan.",
            plan: "Extract key points from recent emails, identify commitments or requests, and formulate a prioritized action plan.",
            actions: [
              "Retrieve last 10 inbox emails",
              "Summarize content: sender, topic, key asks",
              "Tag actions: reply, delegate, file",
              "Prioritize by urgency",
              "Outline plan with deadlines"
            ],
            outcome: "Inbox zero enabler with clear next steps from email volume.",
            steps: [
              "Step 1: Access email API for recent threads.",
              "Step 2: NLP parse for summaries and tasks.",
              "Step 3: Map to action categories.",
              "Step 4: Create bulleted plan.",
              "Step 5: Link back to emails for context."
            ]
          }
        ]
      },
      {
        id: 6,
        title: "Personal Life Balance",
        prompts: [
          {
            id: 601,
            text: "Analyse my last 7 days of personal logs and show stress patterns & wellness score.",
            plan: "Review journal entries for emotional and activity trends, detect stress indicators, and compute a holistic wellness score with insights.",
            actions: [
              "Parse daily logs for mood, events, sleep data",
              "Identify patterns: high-stress triggers, low-energy days",
              "Score wellness (0-100) based on factors like balance, positivity",
              "Visualize trends with charts",
              "Suggest coping strategies"
            ],
            outcome: "Personalized stress awareness report promoting better self-care.",
            steps: [
              "Step 1: Import 7-day log entries.",
              "Step 2: Analyze sentiment and keywords.",
              "Step 3: Weight factors (sleep 30%, mood 40%).",
              "Step 4: Generate score and pattern summary.",
              "Step 5: Recommend actions like breaks or therapy."
            ]
          },
          {
            id: 602,
            text: "Generate my personal day plan based on my goals and work pressure.",
            plan: "Balance long-term goals with current workload to craft a realistic daily schedule optimizing energy and productivity.",
            actions: [
              "Review goal list and work calendar",
              "Assess pressure levels from tasks",
              "Allocate time blocks: deep work, breaks, personal",
              "Prioritize high-impact activities",
              "Include flexibility buffers"
            ],
            outcome: "A harmonious daily itinerary fostering achievement without burnout.",
            steps: [
              "Step 1: Pull goals from tracker, tasks from to-do.",
              "Step 2: Rate work pressure (low/med/high).",
              "Step 3: Time-block using Pomodoro or similar.",
              "Step 4: Integrate personal goals (exercise, family).",
              "Step 5: Output as timeline with reminders."
            ]
          },
          {
            id: 603,
            text: "Show my last 30 days personal expenses and highlight overspending patterns.",
            plan: "Categorize and trend personal spending, flag categories exceeding budgets, and highlight patterns for adjustment.",
            actions: [
              "Import bank/expense app data for 30 days",
              "Classify spends (dining, shopping, etc.)",
              "Compare to budget thresholds",
              "Spot patterns: weekend spikes, impulse buys",
              "Calculate savings potential"
            ],
            outcome: "Expense clarity with targeted tips for financial wellness.",
            steps: [
              "Step 1: Sync transaction history.",
              "Step 2: Auto-categorize using rules/ML.",
              "Step 3: Chart monthly totals and variances.",
              "Step 4: Alert on overspends >20%.",
              "Step 5: Suggest cuts like meal prepping."
            ]
          },
          {
            id: 604,
            text: "Check my habits and create micro-habits for the next 7 days.",
            plan: "Evaluate current habit adherence, identify gaps, and design tiny, buildable micro-habits to reinforce positive routines.",
            actions: [
              "Review habit tracker for consistency",
              "Select 3-5 target areas (e.g., reading, hydration)",
              "Break into micro-actions (2-min versions)",
              "Schedule daily for week with cues",
              "Track progress prompts"
            ],
            outcome: "Sustainable habit-building plan easing into lasting change.",
            steps: [
              "Step 1: Analyze tracker data for streaks/gaps.",
              "Step 2: Choose habits based on goals.",
              "Step 3: Design micro-versions (e.g., 1 page read).",
              "Step 4: Integrate into calendar.",
              "Step 5: Set up daily check-ins."
            ]
          },
          {
            id: 605,
            text: "Summarize my reflection entries and give a mental clarity report.",
            plan: "Synthesize journal reflections for themes, progress, and blocks, delivering a clarity score and growth insights.",
            actions: [
              "Compile recent entries",
              "Extract themes via topic modeling",
              "Assess clarity: resolved vs. recurring issues",
              "Score based on positivity, insight depth",
              "Provide key takeaways and prompts"
            ],
            outcome: "Reflective summary enhancing self-awareness and direction.",
            steps: [
              "Step 1: Gather entries from journal app.",
              "Step 2: Summarize chronologically or thematically.",
              "Step 3: Quantify clarity (e.g., 75% resolved).",
              "Step 4: Highlight wins/challenges.",
              "Step 5: Suggest next reflection focuses."
            ]
          }
        ]
      },
      {
        id: 7,
        title: "Family Life",
        prompts: [
          {
            id: 701,
            text: "Show my family time analysis and suggest a balance plan.",
            plan: "Quantify family interaction time from calendars/logs, identify imbalances, and propose a weekly plan for quality bonding.",
            actions: [
              "Track family events from shared calendar",
              "Calculate hours/week spent together",
              "Compare to ideal (e.g., 10+ hrs)",
              "Suggest activities: dinners, outings",
              "Schedule slots with reminders"
            ],
            outcome: "Balanced family schedule strengthening relationships.",
            steps: [
              "Step 1: Log family time entries.",
              "Step 2: Analyze patterns (workday vs. weekend).",
              "Step 3: Gap analysis vs. goals.",
              "Step 4: Propose 5-7 activities.",
              "Step 5: Add to family calendar."
            ]
          },
          {
            id: 702,
            text: "Give me update on my kids’ school & activities and highlight action areas.",
            plan: "Compile school reports, activity schedules, and performance notes, flagging needs like tutoring or sign-ups.",
            actions: [
              "Pull grades, attendance from school portals",
              "List extracurriculars and progress",
              "Identify lags (e.g., low grades)",
              "Recommend actions: meetings, resources",
              "Summarize in parent dashboard"
            ],
            outcome: "Proactive parenting insights for child development.",
            steps: [
              "Step 1: Sync school/activity data.",
              "Step 2: Per-kid summary.",
              "Step 3: Flag action items.",
              "Step 4: Prioritize by urgency.",
              "Step 5: Set reminders for follow-ups."
            ]
          },
          {
            id: 703,
            text: "Pull upcoming family events & rituals and draft messages + reminders.",
            plan: "Extract calendar events and cultural rituals, create invites/reminders, and draft personalized messages.",
            actions: [
              "Scan calendar for family events",
              "Add ritual dates (birthdays, holidays)",
              "Draft group messages/invites",
              "Set multi-channel reminders",
              "Coordinate RSVPs"
            ],
            outcome: "Seamless event management fostering family traditions.",
            steps: [
              "Step 1: Query shared calendar.",
              "Step 2: Enrich with ritual database.",
              "Step 3: Template messages.",
              "Step 4: Schedule reminders.",
              "Step 5: Distribute via family chat."
            ]
          },
          {
            id: 704,
            text: "Summarise my parents’ health logs & notify pending actions.",
            plan: "Review health tracker entries for parents, summarize trends, and alert on overdue check-ups or meds.",
            actions: [
              "Aggregate vitals, appointments from logs",
              "Spot trends: BP fluctuations",
              "List pending: doctor visits, prescriptions",
              "Notify via preferred method",
              "Suggest preventive steps"
            ],
            outcome: "Elder care oversight ensuring timely interventions.",
            steps: [
              "Step 1: Access shared health app.",
              "Step 2: Monthly summary report.",
              "Step 3: Check against schedules.",
              "Step 4: Generate alerts.",
              "Step 5: Log actions taken."
            ]
          },
          {
            id: 705,
            text: "Show my social interactions and suggest people I should reconnect with.",
            plan: "Analyze contact frequency from comms logs, identify lapsed relationships, and propose reconnection prompts.",
            actions: [
              "Review calls, messages, meets over 6 months",
              "Score relationships by recency/importance",
              "List top 5-10 for outreach",
              "Draft casual check-in messages",
              "Suggest timing/medium"
            ],
            outcome: "Revitalized social network through intentional reconnections.",
            steps: [
              "Step 1: Mine interaction data.",
              "Step 2: Filter dormant contacts.",
              "Step 3: Prioritize by closeness.",
              "Step 4: Personalize messages.",
              "Step 5: Add to task list."
            ]
          }
        ]
      },
      {
        id: 8,
        title: "Married Life",
        prompts: [
          {
            id: 801,
            text: "Analyse 3 months of spouse-related logs and give relationship health score.",
            plan: "Synthesize couple's journal entries for positive/negative themes, compute health score, and offer insights.",
            actions: [
              "Parse logs for interactions, conflicts, joys",
              "Quantify ratios (affection:stress)",
              "Score 0-100 on dimensions: communication, intimacy",
              "Identify patterns/triggers",
              "Recommend boosts"
            ],
            outcome: "Relationship diagnostic for proactive nurturing.",
            steps: [
              "Step 1: Collect 90-day entries.",
              "Step 2: Sentiment analysis.",
              "Step 3: Weighted scoring.",
              "Step 4: Trend visualization.",
              "Step 5: Actionable advice."
            ]
          },
          {
            id: 802,
            text: "Plan a weekly bonding activity based on shared interests.",
            plan: "Match mutual hobbies from profiles, suggest low-effort activity, and schedule it.",
            actions: [
              "List shared interests (movies, hikes)",
              "Propose 3 options with details",
              "Check calendars for slot",
              "Book if needed (reservations)",
              "Send joint invite"
            ],
            outcome: "Regular bonding to sustain marital spark.",
            steps: [
              "Step 1: Query interest database.",
              "Step 2: Filter feasible ideas.",
              "Step 3: Align with availability.",
              "Step 4: Detail plan (time, place).",
              "Step 5: Confirm excitement."
            ]
          },
          {
            id: 803,
            text: "Analyse conflicts and suggest a respectful resolution strategy.",
            plan: "Review dispute logs, empathize perspectives, and outline de-escalation steps.",
            actions: [
              "Summarize recent conflicts",
              "Identify common triggers",
              "Craft strategy: listen, validate, compromise",
              "Role-play scripts",
              "Set follow-up check-in"
            ],
            outcome: "Conflict toolkit for healthier resolutions.",
            steps: [
              "Step 1: Extract conflict entries.",
              "Step 2: Neutral analysis.",
              "Step 3: Build step-by-step guide.",
              "Step 4: Personalize to couple.",
              "Step 5: Practice prompts."
            ]
          },
          {
            id: 804,
            text: "Pull happy moments and create an appreciation message + surprise plan.",
            plan: "Curate positive memories, draft grateful note, and plan small surprise.",
            actions: [
              "Mine logs for joys/milestones",
              "Select 3-5 highlights",
              "Write heartfelt message",
              "Idea surprise (date, gift)",
              "Schedule delivery"
            ],
            outcome: "Amplified marital joy through remembrance.",
            steps: [
              "Step 1: Search positive entries.",
              "Step 2: Timeline curation.",
              "Step 3: Compose message.",
              "Step 4: Brainstorm surprise.",
              "Step 5: Execute discreetly."
            ]
          },
          {
            id: 805,
            text: "Create a 30-day married-life improvement roadmap.",
            plan: "Assess current state, set micro-goals across pillars, track weekly.",
            actions: [
              "Pillars: date nights, communication, support",
              "Daily/weekly tasks (e.g., daily check-in)",
              "Milestones at 10/20/30 days",
              "Progress tracker",
              "Flex adjustments"
            ],
            outcome: "Structured path to deeper connection.",
            steps: [
              "Step 1: Baseline survey.",
              "Step 2: Goal setting.",
              "Step 3: Calendar integration.",
              "Step 4: Resource links.",
              "Step 5: Review cadence."
            ]
          }
        ]
      },
      {
        id: 9,
        title: "Emotional Intelligence Prompts",
        prompts: [
          {
            id: 901,
            text: "Generate an empathetic message to acknowledge someone's feelings.",
            plan: "Craft a response validating emotions without judgment, using active listening phrases.",
            actions: [
              "Identify emotion from context",
              "Use phrases: 'I hear you're feeling...', 'That sounds tough'",
              "Add support offer",
              "Keep concise and sincere"
            ],
            outcome: "Message fostering emotional safety and connection.",
            steps: [
              "Step 1: Parse described feelings.",
              "Step 2: Mirror back accurately.",
              "Step 3: Express care.",
              "Step 4: Invite dialogue.",
              "Step 5: Refine tone."
            ]
          },
          {
            id: 902,
            text: "Draft a comforting sympathy message for someone facing difficulties.",
            plan: "Express condolences with warmth, share subtle hope, avoid clichés.",
            actions: [
              "Acknowledge loss/pain specifically",
              "Offer presence: 'I'm here for you'",
              "End with gentle positivity",
              "Personalize if known"
            ],
            outcome: "Comforting note easing grief.",
            steps: [
              "Step 1: Context-specific opener.",
              "Step 2: Empathetic body.",
              "Step 3: Support closure.",
              "Step 4: Proofread sensitivity.",
              "Step 5: Send timing."
            ]
          },
          {
            id: 903,
            text: "Draft a mature apology message for past conflict.",
            plan: "Own fault without excuses, express remorse, propose amends.",
            actions: [
              "State what/why sorry",
              "Validate their hurt",
              "Commit to change",
              "Open for response"
            ],
            outcome: "Restorative apology rebuilding trust.",
            steps: [
              "Step 1: Admit specific error.",
              "Step 2: Show understanding.",
              "Step 3: Outline fix.",
              "Step 4: Humble tone.",
              "Step 5: Deliver promptly."
            ]
          },
          {
            id: 904,
            text: "Create a celebratory greeting appreciating a friend’s recent win.",
            plan: "Highlight achievement enthusiastically, personalize praise, suggest toast.",
            actions: [
              "Congratulate specifically",
              "Note effort/impact",
              "Add humor/lightness",
              "Propose next meet"
            ],
            outcome: "Uplifting message amplifying success joy.",
            steps: [
              "Step 1: Detail the win.",
              "Step 2: Genuine praise.",
              "Step 3: Emojis/vivacity.",
              "Step 4: Call to celebrate.",
              "Step 5: Timely send."
            ]
          },
          {
            id: 905,
            text: "Create reminders to fulfil commitments made to friends.",
            plan: "Extract promises from convos, set timed reminders, track fulfillment.",
            actions: [
              "Scan messages for commitments",
              "Categorize by deadline",
              "Set app reminders",
              "Log completions",
              "Nudge if nearing due"
            ],
            outcome: "Reliable friendship through kept words.",
            steps: [
              "Step 1: NLP detect promises.",
              "Step 2: Assign dates.",
              "Step 3: Integrate calendar.",
              "Step 4: Auto-follow-up.",
              "Step 5: Review quarterly."
            ]
          }
        ]
      },
      {
        id: 10,
        title: "AI for Time With Friends (Emotional Facets)",
        prompts: [
          {
            id: 1001,
            text: "Analyse last 60 days social interactions and give a social-life score.",
            plan: "Quantify friend meets/calls, sentiment of exchanges, score social health.",
            actions: [
              "Log interactions from apps/calendars",
              "Analyze frequency/depth",
              "Sentiment score convos",
              "Compute overall (e.g., 80/100)",
              "Benchmark norms"
            ],
            outcome: "Social vitality gauge for balance.",
            steps: [
              "Step 1: Data aggregation.",
              "Step 2: Metric calculation.",
              "Step 3: Pattern detection.",
              "Step 4: Score derivation.",
              "Step 5: Improvement tips."
            ]
          },
          {
            id: 1002,
            text: "Create an appreciation message celebrating a friend’s achievements.",
            plan: "Personalize kudos on milestone, recall shared history.",
            actions: [
              "Detail achievement",
              "Link to past support",
              "Express pride",
              "Suggest cheer"
            ],
            outcome: "Heartfelt boost to friend's confidence.",
            steps: [
              "Step 1: Specific praise.",
              "Step 2: Memory tie-in.",
              "Step 3: Warm tone.",
              "Step 4: Share medium.",
              "Step 5: Follow with call."
            ]
          },
          {
            id: 1003,
            text: "Generate empathetic messages based on recent interactions.",
            plan: "Tailor responses to friend's shared struggles, validate feelings.",
            actions: [
              "Review convo history",
              "Match empathy level",
              "Offer solidarity",
              "Probe gently"
            ],
            outcome: "Supportive exchanges deepening bonds.",
            steps: [
              "Step 1: Context recall.",
              "Step 2: Emotion mirror.",
              "Step 3: Care expression.",
              "Step 4: Question open.",
              "Step 5: Send timely."
            ]
          },
          {
            id: 1004,
            text: "Generate a warm supportive message for a friend with sad updates.",
            plan: "Acknowledge sorrow, remind of strength, offer ear.",
            actions: [
              "Validate sadness",
              "Recall positives",
              "Promise availability",
              "Light hope"
            ],
            outcome: "Comfort in vulnerability.",
            steps: [
              "Step 1: Empathize directly.",
              "Step 2: Uplift subtly.",
              "Step 3: Action offer.",
              "Step 4: Soft close.",
              "Step 5: Monitor response."
            ]
          },
          {
            id: 1005,
            text: "Draft a healing & reconnection message for strained interactions.",
            plan: "Bridge gap with humility, express miss, invite reset.",
            actions: [
              "Own part if applicable",
              "Affirm value",
              "Propose meet",
              "No pressure"
            ],
            outcome: "Mended rift, renewed friendship.",
            steps: [
              "Step 1: Gentle opener.",
              "Step 2: Sincere intent.",
              "Step 3: Forward look.",
              "Step 4: Respect space.",
              "Step 5: Hopeful send."
            ]
          },
          {
            id: 1006,
            text: "Pull all commitments made to friends and turn them into reminders.",
            plan: "Mine promises, digitize as tasks with alerts.",
            actions: [
              "Search chat histories",
              "Extract specifics",
              "Set due dates",
              "Categorize friends",
              "Notify fulfillment"
            ],
            outcome: "Integrity in friendships.",
            steps: [
              "Step 1: Keyword search.",
              "Step 2: Detail capture.",
              "Step 3: Task creation.",
              "Step 4: Reminder setup.",
              "Step 5: Close loop."
            ]
          },
          {
            id: 1007,
            text: "Plan meetings with nearby friends based on weekly free slots.",
            plan: "Match locations, availabilities for casual hangs.",
            actions: [
              "Geo-filter friends",
              "Sync calendars",
              "Suggest venues",
              "Propose times",
              "Group polls"
            ],
            outcome: "Spontaneous yet planned connections.",
            steps: [
              "Step 1: Location query.",
              "Step 2: Slot matching.",
              "Step 3: Activity ideas.",
              "Step 4: Invite draft.",
              "Step 5: Confirm."
            ]
          },
          {
            id: 1008,
            text: "Summarise positive interactions & draft a gratitude message.",
            plan: "Highlight good times, thank collectively.",
            actions: [
              "Curate highlights",
              "Theme positives",
              "Write group note",
              "Share medium"
            ],
            outcome: "Gratitude reinforcing ties.",
            steps: [
              "Step 1: Positive log review.",
              "Step 2: Summary craft.",
              "Step 3: Thank specifics.",
              "Step 4: Warm delivery.",
              "Step 5: Cherish response."
            ]
          },
          {
            id: 1009,
            text: "Create a celebratory applause message for a friend's milestones.",
            plan: "Energetic cheer, virtual high-five.",
            actions: [
              "Milestone detail",
              "Exuberant language",
              "Shared joy",
              "Future toast"
            ],
            outcome: "Amplified celebration.",
            steps: [
              "Step 1: Win spotlight.",
              "Step 2: Vivid praise.",
              "Step 3: Emotive flair.",
              "Step 4: Engage back.",
              "Step 5: Post-share."
            ]
          },
          {
            id: 1010,
            text: "Create a 1-year memory timeline with friends.",
            plan: "Chronicle shared moments visually.",
            actions: [
              "Gather photos/events",
              "Timeline build",
              "Caption anecdotes",
              "Share album"
            ],
            outcome: "Nostalgic bond strengthener.",
            steps: [
              "Step 1: Year data mine.",
              "Step 2: Sequence events.",
              "Step 3: Narrative add.",
              "Step 4: Design pretty.",
              "Step 5: Gift send."
            ]
          }
        ]
      },
      {
        id: 11,
        title: "Blue Collar Workers",
        prompts: [
          {
            id: 1101,
            text: "Apply for overseas jobs or work visas",
            plan: "Guide through job search and visa application for international opportunities tailored to skills.",
            actions: [
              "Search global job boards for matching roles",
              "Compile resume and docs",
              "Fill visa forms",
              "Submit applications",
              "Track status"
            ],
            outcome: "Streamlined path to overseas employment.",
            steps: [
              "Step 1: Profile skill assessment.",
              "Step 2: Job matching.",
              "Step 3: Doc prep.",
              "Step 4: Application submit.",
              "Step 5: Follow-up."
            ]
          },
          {
            id: 1102,
            text: "Track salary or remittance issues",
            plan: "Monitor payments, flag discrepancies, resolve with employers/banks.",
            actions: [
              "Log expected vs. received",
              "Alert delays",
              "Contact support",
              "Document disputes",
              "Escalate if needed"
            ],
            outcome: "Timely and accurate earnings.",
            steps: [
              "Step 1: Payment schedule set.",
              "Step 2: Receipt verify.",
              "Step 3: Issue flag.",
              "Step 4: Query send.",
              "Step 5: Resolve log."
            ]
          },
          {
            id: 1103,
            text: "Find nearby accommodation or training programs",
            plan: "Local search for housing/training fitting budget/schedule.",
            actions: [
              "Geo-query options",
              "Filter criteria",
              "Contact listings",
              "Visit schedule",
              "Apply/book"
            ],
            outcome: "Settled living and upskilling.",
            steps: [
              "Step 1: Location input.",
              "Step 2: Option list.",
              "Step 3: Detail fetch.",
              "Step 4: Inquiry.",
              "Step 5: Secure."
            ]
          }
        ]
      },
      {
        id: 12,
        title: "Teachers",
        prompts: [
          {
            id: 1201,
            text: "Enroll in AI teaching skill workshops",
            plan: "Find and register for relevant AI-ed workshops.",
            actions: [
              "Search programs",
              "Eligibility check",
              "Apply/enroll",
              "Prep materials",
              "Attend"
            ],
            outcome: "Enhanced teaching toolkit.",
            steps: [
              "Step 1: Workshop search.",
              "Step 2: Match needs.",
              "Step 3: Register.",
              "Step 4: Study prep.",
              "Step 5: Certify."
            ]
          },
          {
            id: 1202,
            text: "Access global classroom collaboration tools",
            plan: "Recommend and setup international collab platforms.",
            actions: [
              "Tool survey",
              "Account create",
              "Integrate curriculum",
              "Partner connect",
              "Session host"
            ],
            outcome: "Global learning exchanges.",
            steps: [
              "Step 1: Platform pick.",
              "Step 2: Signup.",
              "Step 3: Customize.",
              "Step 4: Network.",
              "Step 5: Launch."
            ]
          },
          {
            id: 1204,
            text: "Track student performance or lesson ideas",
            plan: "Monitor progress, brainstorm engaging lessons.",
            actions: [
              "Data log grades",
              "Trend analyze",
              "Idea generate",
              "Adapt plans",
              "Report share"
            ],
            outcome: "Improved student outcomes.",
            steps: [
              "Step 1: Performance track.",
              "Step 2: Insight draw.",
              "Step 3: Lesson ideate.",
              "Step 4: Implement.",
              "Step 5: Evaluate."
            ]
          }
        ]
      },
      {
        id: 13,
        title: "Students",
        prompts: [
          {
            id: 1301,
            text: "Find scholarships and internships",
            plan: "Match eligibility to opportunities, apply strategically.",
            actions: [
              "Profile build",
              "Search filter",
              "App prep",
              "Submit",
              "Follow"
            ],
            outcome: "Funded opportunities secured.",
            steps: [
              "Step 1: Criteria define.",
              "Step 2: List compile.",
              "Step 3: Docs gather.",
              "Step 4: Apply.",
              "Step 5: Track."
            ]
          },
          {
            id: 1302,
            text: "Ask for study resources or career guidance",
            plan: "Curate materials, advise paths.",
            actions: [
              "Query resources",
              "Recommend paths",
              "Connect mentors",
              "Plan roadmap",
              "Support ongoing"
            ],
            outcome: "Empowered academic/career journey.",
            steps: [
              "Step 1: Need assess.",
              "Step 2: Resource link.",
              "Step 3: Advice give.",
              "Step 4: Goal set.",
              "Step 5: Check-in."
            ]
          },
          {
            id: 1303,
            text: "Join learning communities for new skills",
            plan: "Identify groups, facilitate entry.",
            actions: [
              "Community search",
              "Profile join",
              "Engage intro",
              "Skill thread",
              "Network build"
            ],
            outcome: "Collaborative skill growth.",
            steps: [
              "Step 1: Skill target.",
              "Step 2: Group find.",
              "Step 3: Member become.",
              "Step 4: Contribute.",
              "Step 5: Connect."
            ]
          }
        ]
      },
      {
        id: 14,
        title: "MSMEs",
        prompts: [
          {
            id: 1401,
            text: "Apply for government grants or loans",
            plan: "Navigate schemes, prepare submissions.",
            actions: [
              "Scheme research",
              "Eligibility verify",
              "Docs compile",
              "Form fill",
              "Submit"
            ],
            outcome: "Accessed funding.",
            steps: [
              "Step 1: Grant scan.",
              "Step 2: Match biz.",
              "Step 3: Prep pack.",
              "Step 4: Apply.",
              "Step 5: Status."
            ]
          },
          {
            id: 1402,
            text: "Find skilled workforce or digital tools",
            plan: "Recruit talent, tool recommend.",
            actions: [
              "Job post",
              "Tool evaluate",
              "Hire process",
              "Integrate",
              "Train"
            ],
            outcome: "Efficient operations.",
            steps: [
              "Step 1: Need define.",
              "Step 2: Source.",
              "Step 3: Select.",
              "Step 4: Onboard.",
              "Step 5: Optimize."
            ]
          },
          {
            id: 1403,
            text: "Get export and compliance assistance",
            plan: "Guide regulations, export setup.",
            actions: [
              "Compliance check",
              "Export plan",
              "Doc assist",
              "Partner link",
              "Monitor"
            ],
            outcome: "Global market entry.",
            steps: [
              "Step 1: Reg review.",
              "Step 2: Strategy.",
              "Step 3: Paperwork.",
              "Step 4: Launch.",
              "Step 5: Comply."
            ]
          }
        ]
      },
      {
        id: 15,
        title: "Farmers",
        prompts: [
          {
            id: 1501,
            text: "Get real-time crop advisory and weather alerts",
            plan: "Monitor fields, predict issues, alert.",
            actions: [
              "Weather fetch",
              "Crop data analyze",
              "Advisory generate",
              "Alert push",
              "Act suggest"
            ],
            outcome: "Optimized yields.",
            steps: [
              "Step 1: Field input.",
              "Step 2: Forecast.",
              "Step 3: Tip tailor.",
              "Step 4: Notify.",
              "Step 5: Track."
            ]
          },
          {
            id: 1502,
            text: "Access subsidy or soil testing help",
            plan: "Apply aids, test soil.",
            actions: [
              "Subsidy list",
              "Test kit order",
              "Sample send",
              "Result interpret",
              "Apply"
            ],
            outcome: "Resourceful farming.",
            steps: [
              "Step 1: Aid find.",
              "Step 2: Test arrange.",
              "Step 3: Analyze.",
              "Step 4: Amend.",
              "Step 5: Claim."
            ]
          },
          {
            id: 1503,
            text: "Find buyers or marketplaces for produce",
            plan: "Market connect, sell direct.",
            actions: [
              "Buyer search",
              "Price check",
              "List produce",
              "Negotiate",
              "Deliver"
            ],
            outcome: "Fair profits.",
            steps: [
              "Step 1: Crop ready.",
              "Step 2: Platform join.",
              "Step 3: Post offer.",
              "Step 4: Deal.",
              "Step 5: Ship."
            ]
          }
        ]
      },
      {
        id: 16,
        title: "Women",
        prompts: [
          {
            id: 1601,
            text: "Access women entrepreneurship programs",
            plan: "Enroll in empowering initiatives.",
            actions: [
              "Program scout",
              "Apply",
              "Network",
              "Skill gain",
              "Launch"
            ],
            outcome: "Business empowered.",
            steps: [
              "Step 1: Idea validate.",
              "Step 2: Program select.",
              "Step 3: Join.",
              "Step 4: Learn.",
              "Step 5: Grow."
            ]
          },
          {
            id: 1602,
            text: "Join health and safety initiatives",
            plan: "Connect to wellness/safety nets.",
            actions: [
              "Initiative find",
              "Register",
              "Participate",
              "Resource use",
              "Advocate"
            ],
            outcome: "Secure well-being.",
            steps: [
              "Step 1: Need assess.",
              "Step 2: Group join.",
              "Step 3: Engage.",
              "Step 4: Benefit.",
              "Step 5: Share."
            ]
          },
          {
            id: 1603,
            text: "Explore financial empowerment options",
            plan: "Build wealth literacy and access.",
            actions: [
              "Finance edu",
              "Account open",
              "Invest intro",
              "Plan save",
              "Track"
            ],
            outcome: "Independent finances.",
            steps: [
              "Step 1: Learn basics.",
              "Step 2: Tool setup.",
              "Step 3: Goal set.",
              "Step 4: Act.",
              "Step 5: Review."
            ]
          }
        ]
      },
      {
        id: 17,
        title: "Teenagers",
        prompts: [
          {
            id: 1701,
            text: "Find skill-building games or courses",
            plan: "Curate fun learning for growth.",
            actions: [
              "Interest match",
              "Resource list",
              "Enroll/play",
              "Progress track",
              "Level up"
            ],
            outcome: "Engaged development.",
            steps: [
              "Step 1: Hobby probe.",
              "Step 2: Option pick.",
              "Step 3: Start.",
              "Step 4: Practice.",
              "Step 5: Achieve."
            ]
          },
          {
            id: 1702,
            text: "Ask about social volunteering programs",
            plan: "Link to community service.",
            actions: [
              "Program search",
              "Sign up",
              "Commit time",
              "Reflect",
              "Continue"
            ],
            outcome: "Purposeful contribution.",
            steps: [
              "Step 1: Cause choose.",
              "Step 2: Opp find.",
              "Step 3: Join.",
              "Step 4: Serve.",
              "Step 5: Impact."
            ]
          },
          {
            id: 1703,
            text: "Get guidance on responsible digital habits",
            plan: "Teach balanced online life.",
            actions: [
              "Habit assess",
              "Tip provide",
              "Tool suggest",
              "Challenge set",
              "Monitor"
            ],
            outcome: "Healthy digital citizenship.",
            steps: [
              "Step 1: Usage review.",
              "Step 2: Goal define.",
              "Step 3: Strategy.",
              "Step 4: Implement.",
              "Step 5: Adjust."
            ]
          }
        ]
      },
      {
        id: 18,
        title: "Attendance & Planning",
        prompts: [
          {
            id: 1801,
            text: "Show my attendance records",
            plan: "Display historical attendance with patterns and totals.",
            actions: [
              "Fetch logs",
              "Summarize streaks/absences",
              "Trend chart",
              "Export option"
            ],
            outcome: "Accountability view.",
            steps: [
              "Step 1: Data pull.",
              "Step 2: Calculate metrics.",
              "Step 3: Visualize.",
              "Step 4: Note insights.",
              "Step 5: Share."
            ]
          },
          {
            id: 1802,
            text: "Give me upcoming calendar events",
            plan: "List near-term schedule highlights.",
            actions: [
              "Calendar sync",
              "Filter week/month",
              "Prioritize",
              "Remind set"
            ],
            outcome: "Prepared day.",
            steps: [
              "Step 1: Access events.",
              "Step 2: Sort time.",
              "Step 3: Detail add.",
              "Step 4: Alert.",
              "Step 5: Update."
            ]
          },
          {
            id: 1803,
            text: "List my checklist items",
            plan: "Compile and prioritize pending todos.",
            actions: [
              "Task gather",
              "Category",
              "Rank urgency",
              "Checkbox UI"
            ],
            outcome: "Organized tasks.",
            steps: [
              "Step 1: List compile.",
              "Step 2: Status check.",
              "Step 3: Order.",
              "Step 4: Mark done.",
              "Step 5: Archive."
            ]
          },
          {
            id: 1804,
            text: "Plan my routine and tasks",
            plan: "Build daily flow integrating habits/tasks.",
            actions: [
              "Routine template",
              "Task slot",
              "Buffer add",
              "Energy match"
            ],
            outcome: "Smooth execution.",
            steps: [
              "Step 1: Goals align.",
              "Step 2: Time block.",
              "Step 3: Sequence.",
              "Step 4: Flex.",
              "Step 5: Review."
            ]
          },
          {
            id: 1805,
            text: "Show my goals and achievements",
            plan: "Track progress on set goals.",
            actions: [
              "Goal list",
              "Milestone check",
              "Achievement log",
              "Visual progress"
            ],
            outcome: "Motivated pursuit.",
            steps: [
              "Step 1: Goals recall.",
              "Step 2: Status update.",
              "Step 3: Wins celebrate.",
              "Step 4: Adjust.",
              "Step 5: Next set."
            ]
          }
        ]
      },
      {
        id: 19,
        title: "Daily Life Activities",
        prompts: [
          {
            id: 1901,
            text: "Give me missed opportunities",
            plan: "Reflect on overlooked chances, suggest recoveries.",
            actions: [
              "Log review",
              "Opp spot",
              "Prioritize",
              "Action plan"
            ],
            outcome: "Seized potentials.",
            steps: [
              "Step 1: Day recap.",
              "Step 2: Miss identify.",
              "Step 3: Value assess.",
              "Step 4: Chase.",
              "Step 5: Learn."
            ]
          },
          {
            id: 1902,
            text: "Show my monthly essentials plan",
            plan: "Outline recurring needs budget/schedule.",
            actions: [
              "Essentials list",
              "Cost estimate",
              "Calendar mark",
              "Track spend"
            ],
            outcome: "Covered basics.",
            steps: [
              "Step 1: Category define.",
              "Step 2: Plan build.",
              "Step 3: Budget.",
              "Step 4: Remind.",
              "Step 5: Adjust."
            ]
          },
          {
            id: 1903,
            text: "Review my routine and other tasks",
            plan: "Audit flow, optimize tasks.",
            actions: [
              "Routine map",
              "Task integrate",
              "Efficiency tweak",
              "Feedback"
            ],
            outcome: "Streamlined day.",
            steps: [
              "Step 1: Log analyze.",
              "Step 2: Bottleneck find.",
              "Step 3: Refine.",
              "Step 4: Test.",
              "Step 5: Habituate."
            ]
          },
          {
            id: 1904,
            text: "Display my planner overview",
            plan: "Big-picture week/month view.",
            actions: [
              "Aggregate events/tasks",
              "Visual calendar",
              "Balance check",
              "Export"
            ],
            outcome: "Holistic control.",
            steps: [
              "Step 1: Data merge.",
              "Step 2: View generate.",
              "Step 3: Highlight key.",
              "Step 4: Gap fill.",
              "Step 5: Print/digital."
            ]
          },
          {
            id: 1905,
            text: "Show upcoming travels and visits",
            plan: "Itinerary summary, prep reminders.",
            actions: [
              "Trip list",
              "Detail flesh",
              "Pack list",
              "Alert"
            ],
            outcome: "Stress-free journeys.",
            steps: [
              "Step 1: Calendar filter.",
              "Step 2: Confirm.",
              "Step 3: Prep.",
              "Step 4: Share.",
              "Step 5: Post-review."
            ]
          }
        ]
      },
      {
        id: 20,
        title: "Finance & Assets",
        prompts: [
          {
            id: 2001,
            text: "Show my gifts and digital spending",
            plan: "Track gifting history, digital subs/expenditures.",
            actions: [
              "Gift log",
              "Digital bill pull",
              "Trend analyze",
              "Cancel suggest"
            ],
            outcome: "Thoughtful budgeting.",
            steps: [
              "Step 1: Records fetch.",
              "Step 2: Category sum.",
              "Step 3: Pattern spot.",
              "Step 4: Optimize.",
              "Step 5: Report."
            ]
          },
          {
            id: 2002,
            text: "Display my income, expenses, and subscriptions",
            plan: "Net worth snapshot with inflows/outflows.",
            actions: [
              "Income source",
              "Expense break",
              "Sub list",
              "Balance calc"
            ],
            outcome: "Financial clarity.",
            steps: [
              "Step 1: Data sync.",
              "Step 2: Visualize.",
              "Step 3: Variance.",
              "Step 4: Forecast.",
              "Step 5: Advise."
            ]
          },
          {
            id: 2003,
            text: "Review my investments and loans",
            plan: "Portfolio performance, debt status.",
            actions: [
              "Asset value",
              "Return calc",
              "Loan balance",
              "Risk assess"
            ],
            outcome: "Balanced portfolio.",
            steps: [
              "Step 1: Update values.",
              "Step 2: Analyze.",
              "Step 3: Debt pay plan.",
              "Step 4: Rebalance.",
              "Step 5: Goal align."
            ]
          },
          {
            id: 2004,
            text: "Check my petty accounts",
            plan: "Reconcile small cash transactions.",
            actions: [
              "Petty log",
              "Receipt match",
              "Balance verify",
              "Replenish"
            ],
            outcome: "Petty control.",
            steps: [
              "Step 1: Entries review.",
              "Step 2: Discrepancy find.",
              "Step 3: Correct.",
              "Step 4: Report.",
              "Step 5: Refill."
            ]
          },
          {
            id: 2005,
            text: "Show waste and environmental status",
            plan: "Track eco-footprint, reduction tips.",
            actions: [
              "Waste log",
              "Carbon calc",
              "Habit suggest",
              "Progress track"
            ],
            outcome: "Greener living.",
            steps: [
              "Step 1: Usage data.",
              "Step 2: Impact measure.",
              "Step 3: Tip tailor.",
              "Step 4: Implement.",
              "Step 5: Monitor."
            ]
          }
        ]
      },
      {
        id: 21,
        title: "Emotions & Mental Health",
        prompts: [
          {
            id: 2101,
            text: "Show my dreams and empathy logs",
            plan: "Review subconscious and compassionate acts.",
            actions: [
              "Dream journal",
              "Empathy entries",
              "Theme extract",
              "Insight derive"
            ],
            outcome: "Emotional depth.",
            steps: [
              "Step 1: Logs pull.",
              "Step 2: Pattern see.",
              "Step 3: Reflect.",
              "Step 4: Grow.",
              "Step 5: Share if."
            ]
          },
          {
            id: 2102,
            text: "Review my feelings and moods",
            plan: "Mood trend analysis, triggers.",
            actions: [
              "Entry sentiment",
              "Cycle chart",
              "Cause link",
              "Cope suggest"
            ],
            outcome: "Mood mastery.",
            steps: [
              "Step 1: Data aggregate.",
              "Step 2: Visualize.",
              "Step 3: Correlate.",
              "Step 4: Strategy.",
              "Step 5: Track."
            ]
          },
          {
            id: 2103,
            text: "Summarize my gratitude entries",
            plan: "Compile thanks, amplify positivity.",
            actions: [
              "Gratitude list",
              "Theme group",
              "Impact note",
              "Daily prompt"
            ],
            outcome: "Grateful mindset.",
            steps: [
              "Step 1: Entries collect.",
              "Step 2: Summarize.",
              "Step 3: Celebrate.",
              "Step 4: Continue.",
              "Step 5: Share."
            ]
          },
          {
            id: 2104,
            text: "Display social interactions and sympathy notes",
            plan: "Connection recap, support given.",
            actions: [
              "Interact log",
              "Sympathy review",
              "Quality score",
              "Nurture plan"
            ],
            outcome: "Empathic relations.",
            steps: [
              "Step 1: Social data.",
              "Step 2: Note curate.",
              "Step 3: Assess.",
              "Step 4: Act.",
              "Step 5: Deepen."
            ]
          },
          {
            id: 2105,
            text: "Show my thoughts and regrets",
            plan: "Process reflections, release burdens.",
            actions: [
              "Thought stream",
              "Regret analyze",
              "Lesson learn",
              "Forgive guide"
            ],
            outcome: "Clarity peace.",
            steps: [
              "Step 1: Journal pull.",
              "Step 2: Categorize.",
              "Step 3: Reframe.",
              "Step 4: Release.",
              "Step 5: Forward."
            ]
          }
        ]
      },
      {
        id: 22,
        title: "Hobbies & Entertainment",
        prompts: [
          {
            id: 2201,
            text: "Show gardening and play/sports activities",
            plan: "Log progress, schedule sessions.",
            actions: [
              "Garden track",
              "Sport log",
              "Tip suggest",
              "Group find"
            ],
            outcome: "Joyful pursuits.",
            steps: [
              "Step 1: Activity recall.",
              "Step 2: Progress note.",
              "Step 3: Plan next.",
              "Step 4: Engage.",
              "Step 5: Reflect."
            ]
          },
          {
            id: 2202,
            text: "Display entertainment options",
            plan: "Curate shows/movies based on mood.",
            actions: [
              "Preference match",
              "New release",
              "Playlist make",
              "Watch plan"
            ],
            outcome: "Relaxed unwind.",
            steps: [
              "Step 1: Mood check.",
              "Step 2: Recommend.",
              "Step 3: Queue.",
              "Step 4: Enjoy.",
              "Step 5: Rate."
            ]
          }
        ]
      },
      {
        id: 23,
        title: "Health & Fitness",
        prompts: [
          {
            id: 2301,
            text: "Show my diet routine",
            plan: "Meal log review, nutrition balance.",
            actions: [
              "Intake track",
              "Calorie calc",
              "Deficit spot",
              "Recipe suggest"
            ],
            outcome: "Nourished body.",
            steps: [
              "Step 1: Log fetch.",
              "Step 2: Analyze.",
              "Step 3: Adjust.",
              "Step 4: Plan meals.",
              "Step 5: Track."
            ]
          },
          {
            id: 2302,
            text: "Give me exercise / gym / yoga plan",
            plan: "Personalized routine for fitness level.",
            actions: [
              "Goal assess",
              "Routine build",
              "Schedule",
              "Progress monitor"
            ],
            outcome: "Stronger self.",
            steps: [
              "Step 1: Level test.",
              "Step 2: Program.",
              "Step 3: Calendar.",
              "Step 4: Execute.",
              "Step 5: Adapt."
            ]
          },
          {
            id: 2303,
            text: "Display illness records",
            plan: "Health history summary.",
            actions: [
              "Symptom log",
              "Diagnosis note",
              "Pattern see",
              "Doc remind"
            ],
            outcome: "Proactive health.",
            steps: [
              "Step 1: Records pull.",
              "Step 2: Timeline.",
              "Step 3: Trend.",
              "Step 4: Prevent.",
              "Step 5: Consult."
            ]
          },
          {
            id: 2304,
            text: "Check my medicine schedule",
            plan: "Adherence reminder system.",
            actions: [
              "Prescrip list",
              "Dose time",
              "Alert set",
              "Refill track"
            ],
            outcome: "Compliant wellness.",
            steps: [
              "Step 1: Med catalog.",
              "Step 2: Schedule.",
              "Step 3: Notify.",
              "Step 4: Log intake.",
              "Step 5: Renew."
            ]
          },
          {
            id: 2305,
            text: "Show meditation practice logs",
            plan: "Session review, streak build.",
            actions: [
              "Log entry",
              "Duration avg",
              "Theme suggest",
              "Guided link"
            ],
            outcome: "Calm mind.",
            steps: [
              "Step 1: Practice recall.",
              "Step 2: Metric.",
              "Step 3: Improve.",
              "Step 4: Session.",
              "Step 5: Journal."
            ]
          },
          {
            id: 2306,
            text: "Display physical growth and vaccination status",
            plan: "Milestone track, vax update.",
            actions: [
              "Measure log",
              "Vax schedule",
              "Gap fill",
              "Chart growth"
            ],
            outcome: "Healthy progress.",
            steps: [
              "Step 1: Data enter.",
              "Step 2: Status check.",
              "Step 3: Alert due.",
              "Step 4: Vaccinate.",
              "Step 5: Record."
            ]
          },
          {
            id: 2307,
            text: "Check my pet care activities",
            plan: "Routine ensure, vet remind.",
            actions: [
              "Feed/walk log",
              "Health check",
              "Groom schedule",
              "Play time"
            ],
            outcome: "Happy pet.",
            steps: [
              "Step 1: Care review.",
              "Step 2: Task list.",
              "Step 3: Remind.",
              "Step 4: Do.",
              "Step 5: Note."
            ]
          }
        ]
      },
      {
        id: 24,
        title: "Food & Cooking",
        prompts: [
          {
            id: 2401,
            text: "Show cooking recipes and plans",
            plan: "Meal prep calendar with recipes.",
            actions: [
              "Recipe bank",
              "Week plan",
              "Shop list",
              "Cook guide"
            ],
            outcome: "Tasty efficiency.",
            steps: [
              "Step 1: Preference.",
              "Step 2: Menu set.",
              "Step 3: Ingredients.",
              "Step 4: Steps.",
              "Step 5: Enjoy."
            ]
          },
          {
            id: 2402,
            text: "Display food consumption and nutrition",
            plan: "Intake analysis, balance tips.",
            actions: [
              "Meal log",
              "Nutrient break",
              "Deficit",
              "Suggest"
            ],
            outcome: "Balanced diet.",
            steps: [
              "Step 1: Track eat.",
              "Step 2: Calc.",
              "Step 3: Visualize.",
              "Step 4: Adjust.",
              "Step 5: Maintain."
            ]
          }
        ]
      },
      {
        id: 25,
        title: "Motherhood Memories",
        prompts: [
          {
            id: 2501,
            text: "Show my motherhood memories",
            plan: "Curate timeline of moments.",
            actions: [
              "Photo/journal pull",
              "Chronicle",
              "Theme",
              "Share family"
            ],
            outcome: "Cherished recall.",
            steps: [
              "Step 1: Collect.",
              "Step 2: Organize.",
              "Step 3: Narrate.",
              "Step 4: Visualize.",
              "Step 5: Treasure."
            ]
          }
        ]
      },
      {
        id: 26,
        title: "Teenagers",
        prompts: [
          {
            id: 2601,
            text: "Show skill-building games or courses",
            plan: "Fun learning recs.",
            actions: [
              "Skill interest",
              "Game/course find",
              "Enroll",
              "Play/learn"
            ],
            outcome: "Skill fun.",
            steps: [
              "Step 1: Want.",
              "Step 2: Pick.",
              "Step 3: Start.",
              "Step 4: Progress.",
              "Step 5: Master."
            ]
          },
          {
            id: 2602,
            text: "Give me social volunteering program options",
            plan: "Cause-aligned service.",
            actions: [
              "Interest match",
              "Program list",
              "Join",
              "Serve"
            ],
            outcome: "Impact youth.",
            steps: [
              "Step 1: Passion.",
              "Step 2: Options.",
              "Step 3: Commit.",
              "Step 4: Act.",
              "Step 5: Reflect."
            ]
          },
          {
            id: 2603,
            text: "Provide guidance on responsible digital habits",
            plan: "Safe online tips.",
            actions: [
              "Habit audit",
              "Rule set",
              "Tool",
              "Challenge"
            ],
            outcome: "Smart digital.",
            steps: [
              "Step 1: Check use.",
              "Step 2: Guide.",
              "Step 3: Apply.",
              "Step 4: Track.",
              "Step 5: Balance."
            ]
          }
        ]
      }
    ]
  }
];

export { agenticAIIntro, agenticAIPromptData };
